import React, { useEffect, useRef, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Alert, AppState, AppStateStatus,
  DeviceEventEmitter, EmitterSubscription, Linking, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';
import * as Speech from 'expo-speech';
import { LOCATION_TASK_NAME, LOCATION_EVENT } from '../../tasks/locationTask';
import type { LocationEventData } from '../../tasks/locationTask';
import { appLog } from '../../utils/logger';

type GeoPosition = LocationEventData;
import MapView, { Marker, Polyline } from 'react-native-maps';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RunStackParamList } from '../../navigation/AppNavigator';
import {
  useActiveRunStore, useLandmarksStore, usePresetRunsStore,
  useSettingsStore, useUserStore,
} from '../../store';
import { runsApi } from '../../api';
import { findNearbyLandmark } from '../../utils/proximity';
import { totalDistanceMiles, paceMinsPerMile } from '../../utils/geo';
import { announceSegment, buildFirstLandmarkText } from '../../utils/tts';
import type { Landmark, RunSegment } from '../../types';
import { generateId } from '../../utils/id';

type Props = NativeStackScreenProps<RunStackParamList, 'ActiveRun'>;

export default function ActiveRunScreen({ route, navigation }: Props) {
  const { mode, presetRunId } = route.params;
  const mapRef = useRef<MapView>(null);
  const [mapReady, setMapReady] = useState(false);
  const initialCoordRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const locationEventSub = useRef<EmitterSubscription | null>(null);
  // Ref to always-current position handler — avoids stale closure in GPS subscription
  const onPositionRef = useRef<((pos: GeoPosition) => void) | null>(null);
  // Ref to latest activeRun value for use in stable callbacks (appState handler)
  const activeRunRef = useRef(activeRun);
  // Autopause tracking
  const lastMovingAtRef = useRef<number>(Date.now());
  const isAutopausedRef = useRef(false);
  // Edge-trigger proximity: key of the landmark/index the user is currently inside.
  // Announcements fire only on the transition from null → key (entering the circle).
  const nearbyKeyRef = useRef<string | null>(null);
  // Throttle GPS log entries to one every 30 seconds
  const lastGpsLogRef = useRef<number>(0);
  // Count GPS events received by the screen handler (separate from task-side count)
  const gpsEventCountRef = useRef<number>(0);
  // Stale event batch handling — debounce to process only the most recent position
  const staleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestStalePosRef = useRef<{ lat: number; lng: number; ts: number } | null>(null);
  const staleEventBatchCountRef = useRef<number>(0);
  // Serialize proximity checks — prevents concurrent async calls from stale batches
  const isProcessingProximityRef = useRef(false);
  // Deduplicate AppState events (Android can fire the same state multiple times rapidly)
  const lastAppStateRef = useRef<AppStateStatus>(AppState.currentState);
  // Guard against concurrent startWatching() calls (rapid AppState toggling causes multiple)
  const isStartingWatchRef = useRef(false);

  const { landmarks } = useLandmarksStore();
  const { presetRuns } = usePresetRunsStore();
  const { settings } = useSettingsStore();
  const { profile } = useUserStore();
  const {
    activeRun, startRun, updateGpx, advanceLandmark, addSegment,
    pauseRun, resumeRun, endRun,
  } = useActiveRunStore();

  // Keep ref in sync so stable callbacks always see latest activeRun
  activeRunRef.current = activeRun;

  // Tick every second so elapsed time display updates without waiting for GPS
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!activeRun) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [activeRun?.startedAt]);

  // Initialize the run on mount
  useEffect(() => {
    initRun();
    return () => stopWatching();
  }, []);

  // Track app state to handle backgrounding
  useEffect(() => {
    const sub = AppState.addEventListener('change', handleAppState);
    return () => sub.remove();
  }, []);

  function handleAppState(state: AppStateStatus) {
    // Android fires duplicate AppState events rapidly — ignore if state hasn't changed
    if (state === lastAppStateRef.current) return;
    const prev = lastAppStateRef.current;
    lastAppStateRef.current = state;
    const label =
      state === 'active'     ? 'App foregrounded' :
      state === 'background' ? 'App backgrounded' :
                               `App state → ${state}`;
    appLog.info(label, { from: prev, to: state });
    if (state === 'active' && activeRunRef.current) {
      // Reconnect event listener if it was dropped
      if (!locationEventSub.current) {
        startWatching();
        return;
      }
      // Check whether the background task is still alive (Android can kill it)
      Location.hasStartedLocationUpdatesAsync(LOCATION_TASK_NAME)
        .then((isRunning) => {
          appLog.info('Task health check', { isRunning });
          if (!isRunning) {
            appLog.warn('Location task died in background — restarting');
            startLocationTaskWithRetry().catch((e) =>
              appLog.error('Task restart failed', { error: e?.message })
            );
          }
        })
        .catch(() => {});
    }
  }

  async function initRun() {
    appLog.info('initRun', { mode, presetRunId });

    // One-time advisory to disable battery optimization (Android Doze suspends JS thread)
    if (Platform.OS === 'android') {
      const shown = await AsyncStorage.getItem('@lmr_battery_opt_shown').catch(() => null);
      if (!shown) {
        await AsyncStorage.setItem('@lmr_battery_opt_shown', '1').catch(() => {});
        Alert.alert(
          '⚡ Enable Background Tracking',
          'For announcements to work when your screen is off:\n\nSettings → Apps → Landmark Run → Battery → Unrestricted\n\nThis prevents Android from suspending the app.',
          [
            { text: 'Open Settings', onPress: () => Linking.openSettings() },
            { text: 'Later', style: 'cancel' },
          ]
        );
      }
    }

    // Snap current position immediately so map centres before first background event fires
    Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced })
      .then((pos) => {
        initialCoordRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        if (mapRef.current) {
          mapRef.current.animateCamera({
            center: initialCoordRef.current,
            zoom: 17,
          });
        }
      })
      .catch(() => {});

    let runLandmarks: Landmark[] = [];
    let preset = undefined;

    if (mode === 'preset' && presetRunId) {
      preset = presetRuns.find((r) => r.presetRunId === presetRunId);
      if (!preset) { Alert.alert('Error', 'Preset run not found.'); navigation.goBack(); return; }
      runLandmarks = preset.landmarkIds
        .map((id) => landmarks.find((l) => l.landmarkId === id))
        .filter(Boolean) as Landmark[];
    } else {
      // Free run: use all landmarks (own + public)
      runLandmarks = landmarks;
    }

    try {
      const { runId } = await runsApi.start({ mode, presetRunId });
      startRun({
        runId,
        mode,
        presetRun: preset,
        landmarks: runLandmarks,
        nextLandmarkIndex: 0,
        segments: [],
        startedAt: Date.now(),
        gpxPoints: [],
        totalPausedMs: 0,
        segmentPausedMs: 0,
      });
      await startWatching();
    } catch (e: any) {
      Alert.alert('Failed to start run', e.message ?? 'Unknown error');
      navigation.goBack();
    }
  }

  async function startWatching() {
    // Prevent concurrent calls — rapid AppState toggling can trigger multiple starts
    // which each call startLocationUpdatesAsync, restarting and destabilising the task
    appLog.info('startWatching called', { alreadyStarting: isStartingWatchRef.current });
    if (isStartingWatchRef.current) {
      appLog.info('startWatching skipped (already starting)');
      return;
    }
    isStartingWatchRef.current = true;
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        appLog.warn('Foreground location permission denied');
        return;
      }

      // Request background permission (optional — grant gives better tracking)
      const bgResult = await Location.requestBackgroundPermissionsAsync().catch(() => ({ status: 'error' }));
      appLog.info('Background location permission', { status: bgResult.status });

      // Attach event listener (removes any stale one first)
      locationEventSub.current?.remove();
      locationEventSub.current = DeviceEventEmitter.addListener(
        LOCATION_EVENT,
        (data: GeoPosition) => onPositionRef.current?.(data)
      );
      appLog.info('GPS event listener attached');

      // Start background-capable location task if not already running
      const isRunning = await Location.hasStartedLocationUpdatesAsync(LOCATION_TASK_NAME).catch(() => false);
      if (!isRunning) {
        await startLocationTaskWithRetry();
      } else {
        appLog.info('Location task already running, skipping start');
      }
    } finally {
      isStartingWatchRef.current = false;
    }
  }

  /** Android 12+ forbids starting a foreground service while the app is in the background.
   *  Retry once after a short delay — by then the run screen is fully in the foreground. */
  async function startLocationTaskWithRetry(attempt = 1) {
    const opts = {
      accuracy: Location.Accuracy.High,
      timeInterval: 2000,
      distanceInterval: 0,
      foregroundService: {
        notificationTitle: 'Landmark Run',
        notificationBody: 'Tracking your run in the background…',
        notificationColor: '#1a73e8',
      },
      pausesUpdatesAutomatically: false,
      showsBackgroundLocationIndicator: true,
    };
    try {
      await Location.startLocationUpdatesAsync(LOCATION_TASK_NAME, opts);
      appLog.info('Location task started', { attempt });
    } catch (e: any) {
      const isBgError = e?.message?.toLowerCase().includes('background');
      if (attempt === 1 && isBgError) {
        appLog.warn('Foreground service start failed (app in background), retrying in 1s…', { error: e.message });
        await new Promise((r) => setTimeout(r, 1000));
        return startLocationTaskWithRetry(2);
      }
      throw e; // re-throw on second attempt or unrelated errors
    }
  }

  function stopWatching() {
    appLog.info('stopWatching called');
    locationEventSub.current?.remove();
    locationEventSub.current = null;
    Location.stopLocationUpdatesAsync(LOCATION_TASK_NAME).catch(() => {});
  }

  // Assign the latest handler to the ref on every render — the GPS subscription
  // always calls onPositionRef.current, so it always has the latest closure
  onPositionRef.current = (pos: GeoPosition) => {
    const { latitude: lat, longitude: lng, speed } = pos.coords;
    const ts = pos.timestamp;
    const now = Date.now();
    let justResumed = false;

    gpsEventCountRef.current += 1;
    const eventCount = gpsEventCountRef.current;

    // Stale event guard: when the JS thread is throttled (screen off), GPS events
    // can arrive late. We allow up to 30 seconds of latency before treating an
    // event as too stale for proximity detection. The nearbyKeyRef edge-trigger
    // prevents duplicate announcements even if multiple stale events arrive at once.
    const eventAgeMs = now - ts;
    const isStale = eventAgeMs > 30_000;

    // Throttle detailed GPS log to once every 30 seconds — fires for both fresh and stale
    const GPS_LOG_INTERVAL_MS = 30_000;
    if (now - lastGpsLogRef.current >= GPS_LOG_INTERVAL_MS) {
      lastGpsLogRef.current = now;
      const speedMph = speed != null ? (speed * 2.237).toFixed(1) : '?';
      appLog.info(`GPS ${lat.toFixed(5)}, ${lng.toFixed(5)} · ${speedMph} mph`, {
        paused: !!activeRun?.pausedAt,
        stale: isStale,
        eventAgeMs: Math.round(eventAgeMs),
        totalEventsReceived: eventCount,
      });
    }

    if (isStale) {
      // Record position in GPX trail regardless of age
      if (activeRun && activeRun.pausedAt === undefined) updateGpx(lat, lng, ts);

      // Batch stale events and process only the most recent position.
      // Android suspends the JS thread (even with foreground service) and delivers
      // all queued GPS events at once when the screen turns on. Processing all 90+
      // events individually would cause concurrent proximity checks and TTS spam.
      staleEventBatchCountRef.current += 1;
      latestStalePosRef.current = { lat, lng, ts };
      if (staleDebounceRef.current) clearTimeout(staleDebounceRef.current);
      staleDebounceRef.current = setTimeout(() => {
        const latest = latestStalePosRef.current;
        const count = staleEventBatchCountRef.current;
        if (latest && activeRunRef.current && activeRunRef.current.pausedAt === undefined) {
          const ageS = Math.round((Date.now() - latest.ts) / 1000);
          appLog.info(`GPS stale batch: ${count} events — checking proximity at most recent (${ageS}s old)`);
          checkProximity(latest.lat, latest.lng, latest.ts);
        }
        staleDebounceRef.current = null;
        latestStalePosRef.current = null;
        staleEventBatchCountRef.current = 0;
      }, 300);
      return;
    }

    // ── Autopause logic ───────────────────────────────────────────────────────
    if (settings.autopause && activeRun) {
      const AUTOPAUSE_THRESHOLD_MS = 3000; // stop for 3s before pausing
      const MOVING_SPEED_MS = 0.5;         // m/s (~1 mph)

      if (speed >= MOVING_SPEED_MS) {
        lastMovingAtRef.current = now;
        // Auto-resume if currently autopaused
        if (isAutopausedRef.current && activeRun.pausedAt !== undefined) {
          isAutopausedRef.current = false;
          resumeRun(now);
          justResumed = true;
        }
      } else if (!isAutopausedRef.current && activeRun.pausedAt === undefined &&
                 now - lastMovingAtRef.current > AUTOPAUSE_THRESHOLD_MS) {
        isAutopausedRef.current = true;
        pauseRun(now);
        return; // don't add GPX point while pausing
      }
    }

    // Skip GPS updates while paused — but NOT if we just auto-resumed this event
    if (activeRun?.pausedAt !== undefined && !justResumed) {
      appLog.info(`GPS event #${eventCount} skipped (run paused)`);
      return;
    }

    updateGpx(lat, lng, ts);
    checkProximity(lat, lng, ts);
    if (mapRef.current) {
      mapRef.current.animateCamera({ center: { latitude: lat, longitude: lng }, zoom: 17 });
    }
  };

  async function checkProximity(lat: number, lng: number, ts: number) {
    if (!activeRun) return;
    // Prevent concurrent calls (e.g. fresh event arriving while stale batch is processing)
    if (isProcessingProximityRef.current) return;
    isProcessingProximityRef.current = true;
    try {
      const candidateLandmarks =
        mode === 'preset'
          ? activeRun.landmarks.slice(activeRun.nextLandmarkIndex, activeRun.nextLandmarkIndex + 1)
          : activeRun.landmarks;

      const nearby = findNearbyLandmark(lat, lng, candidateLandmarks, settings.proximityRadiusMeters);

      // ── Edge-trigger: only announce on transition into a proximity circle ──────
      // Key encodes the specific position (preset index or free landmark ID) so
      // re-entry after leaving fires again, but standing still does NOT repeat.
      const currentKey = nearby
        ? (mode === 'preset' ? `preset-${activeRun.nextLandmarkIndex}` : `free-${nearby.landmarkId}`)
        : null;

      if (currentKey === nearbyKeyRef.current) {
        // No change — either still in same circle, or still outside all circles
        return;
      }
      const enteringCircle = currentKey !== null;
      nearbyKeyRef.current = currentKey;

      if (!enteringCircle || !nearby) {
        appLog.info(`Left proximity circle`, { lat, lng });
        return; // user just left a circle — nothing to announce
      }

      appLog.info(`Entered proximity circle`, { landmark: nearby.name, mode, key: currentKey });

      const prev = activeRun.lastPassedLandmark;
      advanceLandmark(nearby, ts);

      if (!prev) {
        // First landmark — announce name + overall run stats since start
        const totalDurSecs = Math.round((ts - activeRun.startedAt) / 1000);
        const totalDist = totalDistanceMiles(activeRun.gpxPoints);
        const avgPace = totalDist > 0 ? paceMinsPerMile(totalDist, totalDurSecs) : null;
        appLog.info(`Landmark reached (first): ${nearby.name}`, {
          totalDistMiles: totalDist.toFixed(4),
          totalDurSecs,
          avgPaceMinsPerMile: avgPace?.toFixed(3) ?? 'n/a',
        });
        speak(buildFirstLandmarkText(nearby, totalDist, totalDurSecs, settings));
      } else if (prev.landmarkId === nearby.landmarkId) {
        // Re-entered the same circle without passing another landmark — nothing to announce
        appLog.info(`Re-entered same landmark circle, skipping segment`, { landmark: nearby.name });
      } else {
        // Later landmark — build segment stats
        const prevTs = activeRun.lastPassedAt ?? activeRun.startedAt;
        const segmentPoints = activeRun.gpxPoints.filter((p) => p.ts >= prevTs && p.ts <= ts);
        const distMiles = totalDistanceMiles(segmentPoints);
        // Subtract any paused time within this segment
        const durationSecs = Math.max(1,
          Math.round((ts - prevTs) / 1000) - Math.round(activeRun.segmentPausedMs / 1000)
        );
        const pace = paceMinsPerMile(distMiles || 0.001, durationSecs);

        const segment: RunSegment = {
          fromLandmarkId: prev.landmarkId,
          toLandmarkId: nearby.landmarkId,
          fromLandmarkName: prev.name,
          toLandmarkName: nearby.name,
          distanceMiles: distMiles,
          durationSecs,
          paceMinsPerMile: pace,
        };
        addSegment(segment);

        // Persist segment to backend
        try { await runsApi.addSegment(activeRun.runId, segment); } catch { /* non-fatal */ }

        appLog.info(`Landmark reached: ${prev.name} → ${nearby.name}`, {
          distMiles: distMiles.toFixed(4),
          durationSecs,
          paceMinsPerMile: pace.toFixed(3),
        });

        // Detailed segment announcement — fires via foreground service regardless of screen state.
        await announceSegment(
          segment,
          settings,
          profile?.userId ?? '',
          profile?.ageGroup,
          profile?.gender,
          profile?.groups[0],
          speak
        );
      }
    } finally {
      isProcessingProximityRef.current = false;
    }
  }

  /** Speak via the system TTS engine. Works in background via the foreground service. */
  function speak(text: string) {
    appLog.info(`TTS: "${text}"`);
    // Stop any in-progress speech before starting a new announcement
    Speech.stop();
    Speech.speak(text, {
      language: 'en-US',
      rate: 0.5,
      pitch: 1.0,
      onStart:   () => appLog.info('TTS started'),
      onDone:    () => appLog.info('TTS done'),
      onStopped: () => appLog.info('TTS stopped early'),
      onError:   (e) => appLog.error('TTS error', { message: e?.message }),
    });
  }

  async function handleFinish() {
    Alert.alert('Finish Run', 'End this run?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Finish', onPress: async () => {
          stopWatching();
          if (!activeRun) { endRun(); navigation.goBack(); return; }
          const now = new Date().toISOString();
          const totalPoints = activeRun.gpxPoints;
          const totalDist = totalDistanceMiles(totalPoints);
          // Exclude accumulated pause time + any currently-active pause
          const activePauseMs = activeRun.pausedAt ? Date.now() - activeRun.pausedAt : 0;
          const totalDur = Math.max(0, Math.round(
            (Date.now() - activeRun.startedAt - activeRun.totalPausedMs - activePauseMs) / 1000
          ));
          try {
            await runsApi.finish(activeRun.runId, {
              completedAt: now,
              totalDistanceMiles: totalDist,
              totalDurationSecs: totalDur,
            });
          } catch { /* non-fatal */ }
          endRun();
          navigation.replace('RunHistory');
        },
      },
    ]);
  }

  const isPaused = !!(activeRun?.pausedAt);
  const isAutopaused = isPaused && isAutopausedRef.current;

  function handlePauseResume() {
    if (!activeRun) return;
    if (isPaused) {
      // Manual resume
      isAutopausedRef.current = false;
      lastMovingAtRef.current = Date.now();
      resumeRun(Date.now());
    } else {
      // Manual pause — GPS task keeps running (needed for map and auto-resume detection)
      pauseRun(Date.now());
      isAutopausedRef.current = false;
    }
  }

  // Elapsed time subtracts accumulated pause time + current ongoing pause
  const activePauseMs = activeRun?.pausedAt ? Date.now() - activeRun.pausedAt : 0;
  const elapsed = activeRun
    ? Math.max(0, Math.round((Date.now() - activeRun.startedAt - (activeRun.totalPausedMs ?? 0) - activePauseMs) / 1000))
    : 0;
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const gpxCoords = (activeRun?.gpxPoints ?? []).map((p) => ({ latitude: p.lat, longitude: p.lng }));

  return (
    <View style={styles.container}>
      <MapView
        ref={mapRef}
        style={styles.map}
        showsUserLocation
        followsUserLocation={false}
        onMapReady={() => {
          setMapReady(true);
          if (initialCoordRef.current && mapRef.current) {
            mapRef.current.animateCamera({ center: initialCoordRef.current, zoom: 17 });
          }
        }}
      >
        {(activeRun?.landmarks ?? []).map((lm) => (
          <Marker
            key={lm.landmarkId}
            coordinate={{ latitude: lm.lat, longitude: lm.lng }}
            title={lm.name}
            pinColor={activeRun?.lastPassedLandmark?.landmarkId === lm.landmarkId ? '#34a853' : '#1a73e8'}
          />
        ))}
        {gpxCoords.length > 1 && (
          <Polyline coordinates={gpxCoords} strokeColor="#1a73e8" strokeWidth={3} />
        )}
      </MapView>

      <View style={styles.hud}>
        <View style={styles.hudRow}>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</Text>
            <Text style={styles.statLabel}>Time</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{totalDistanceMiles(activeRun?.gpxPoints ?? []).toFixed(2)}</Text>
            <Text style={styles.statLabel}>Miles</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{activeRun?.segments.length ?? 0}</Text>
            <Text style={styles.statLabel}>Segments</Text>
          </View>
        </View>

        {isAutopaused && (
          <Text style={styles.autopausedLabel}>⏸ Autopaused — start moving to resume</Text>
        )}

        {activeRun?.lastPassedLandmark && (
          <Text style={styles.lastPassed}>
            Last passed: <Text style={styles.lastPassedName}>{activeRun.lastPassedLandmark.name}</Text>
          </Text>
        )}

        {mode === 'preset' && activeRun && (
          <Text style={styles.nextUp}>
            Next: {activeRun.landmarks[activeRun.nextLandmarkIndex]?.name ?? 'Finish!'}
          </Text>
        )}

        <View style={styles.buttonRow}>
          {!isAutopaused && (
            <TouchableOpacity
              style={[styles.pauseButton, isPaused && styles.resumeButton]}
              onPress={handlePauseResume}
            >
              <Text style={styles.pauseText}>{isPaused ? '▶ Resume' : '⏸ Pause'}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.finishButton, isAutopaused && styles.finishButtonFull]}
            onPress={handleFinish}
          >
            <Text style={styles.finishText}>■ Finish Run</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  map: { flex: 1 },
  hud: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderTopLeftRadius: 20, borderTopRightRadius: 20,
    padding: 20, paddingBottom: 36,
    elevation: 10,
  },
  hudRow: { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 12 },
  stat: { alignItems: 'center' },
  statValue: { fontSize: 28, fontWeight: '700', color: '#1a73e8' },
  statLabel: { fontSize: 11, color: '#888', marginTop: 2 },
  lastPassed: { textAlign: 'center', color: '#555', fontSize: 13, marginBottom: 4 },
  lastPassedName: { fontWeight: '700', color: '#34a853' },
  nextUp: { textAlign: 'center', color: '#1a73e8', fontWeight: '600', fontSize: 14, marginBottom: 8 },
  autopausedLabel: {
    textAlign: 'center', color: '#f4a100', fontWeight: '600', fontSize: 13, marginBottom: 6,
  },
  buttonRow: { flexDirection: 'row', gap: 8, marginTop: 4, alignItems: 'stretch' },
  pauseButton: {
    flex: 1, height: 52, backgroundColor: '#5f6368', borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  resumeButton: { backgroundColor: '#34a853' },
  pauseText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  finishButton: {
    flex: 1, height: 52, backgroundColor: '#ea4335', borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  finishButtonFull: { flex: 1 },
  finishText: { color: '#fff', fontWeight: '700', fontSize: 16 },
});
